import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { posts } from './postsData';
import { stripHtml } from './utils';

import './PostDetail.css';

const createOrUpdateMeta = (attrName, attrValue, content) => {
  let selector = attrName === 'name' || attrName === 'property'
    ? `meta[${attrName}="${attrValue}"]`
    : `link[rel="${attrValue}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(attrName === 'name' || attrName === 'property' ? 'meta' : 'link');
    if (attrName === 'name' || attrName === 'property') {
      element.setAttribute(attrName, attrValue);
    } else {
      element.setAttribute('rel', attrValue);
    }
    document.head.appendChild(element);
  }

  if (attrName === 'name' || attrName === 'property') {
    element.setAttribute('content', content);
  } else {
    element.setAttribute('href', content);
  }

  return element;
};

const createOrUpdateScript = (id, json) => {
  let script = document.head.querySelector(`script[id="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(json);
  return script;
};

const PostDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = posts.find(p => p.id === slug);
  const [isCopied, setIsCopied] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.title,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      // Fallback for browsers that don't support navigator.share
      navigator.clipboard.writeText(window.location.href).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    }
  };

  useEffect(() => {
    if (!post) return;

    document.title = `${post.title} | Arktemis`;
    createOrUpdateMeta('name', 'description', stripHtml(post.content).substring(0, 160));
    createOrUpdateMeta('rel', 'canonical', `https://arktemis.com/posts/${post.id}`);
    createOrUpdateMeta('property', 'og:title', post.title);
    createOrUpdateMeta('property', 'og:description', stripHtml(post.content).substring(0, 160));
    createOrUpdateMeta('property', 'og:url', `https://arktemis.com/posts/${post.id}`);
    createOrUpdateMeta('property', 'og:type', 'article');
    createOrUpdateMeta('name', 'twitter:card', 'summary_large_image');
    createOrUpdateMeta('name', 'twitter:title', post.title);
    createOrUpdateMeta('name', 'twitter:description', stripHtml(post.content).substring(0, 160));

    createOrUpdateScript('article-schema', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: stripHtml(post.content).substring(0, 160),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://arktemis.com/posts/${post.id}`,
      },
      author: {
        '@type': 'Organization',
        name: 'Arktemis Foundation',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Arktemis Foundation',
      },
      url: `https://arktemis.com/posts/${post.id}`,
    });
  }, [post]);

  if (!post) {
    return <div className="bg-black container mx-auto p-4 pt-20 text-white min-h-screen">Post not found.</div>;
  }

  return (
    <>
      <div className="bg-black text-white pt-20 min-h-screen py-12">
        <div className="container mx-auto px-4">
          <div className="pt-6 flex justify-between items-center" data-aos="fade-right">
            <button
              onClick={() => navigate('/posts')}
              className="mb-8 inline-block px-6 py-2 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-black transition-colors duration-300"
            >
              &larr; Back to Posts
            </button>
            <button
              onClick={handleShare}
              className="relative mb-8 inline-flex items-center justify-center p-1 overflow-hidden text-lg font-medium text-gray-900 rounded-lg group bg-gradient-to-br from-green-600 to-blue-500 group-hover:from-purple-600 group-hover:to-blue-500 hover:text-white dark:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95"
            >
              <span className="relative px-6 py-3 transition-all ease-in duration-150 bg-white dark:bg-gray-900 rounded-md group-hover:bg-opacity-0 flex items-center">
                {isCopied ? (
                  <i className="fas fa-check text-green-400"></i>
                ) : (
                  <>
                    <i className="fas fa-share-alt mr-2"></i> Share
                  </>
                )}
              </span>
            </button>
          </div>
          <article className="bg-black-900 p-6 md:p-10 rounded-lg shadow-2xl border border-gray-700" data-aos="fade-up">
            <div className="post-container">
              <header className="mb-8">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-green-400 leading-tight">{post.title}</h1>
              </header>
              <div
                className="post-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              <footer className="text-center mt-8 text-gray-500">
                <p>© 2025 Arktemis Foundation</p>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default PostDetail;